import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Text,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Button,
  HStack,
  VStack,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Progress,
  Tooltip,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Image,
  Textarea,
  Icon,
} from "@chakra-ui/react";
import { GrDownload } from "react-icons/gr";
import { AiOutlineFile } from "react-icons/ai";
import { Cancel } from "@mui/icons-material";
import * as XLSX from "xlsx";
import {
  ValidationError,
  object as YupObject,
  string as YupString,
  number as YupNumber,
} from "yup";
import axios from "axios";
import { placeEnquiryEndpoints } from "./utils/placeEnquiryEndpoints";
import {
  ellipsizeText,
  getFileName,
  getFileExt,
} from "./utils/placeEnquiryUtil";
import { usePlaceEnquiryData } from "./hooks/usePlaceEnquiryData";
import GaraazSearch from "./GaraazSearch";
import GaraazPartConversion, { PartTableRow } from "./GaraazPartConversion";
import VehicleInfoCard from "./VehicleInfoCard";
import BillingDetails, {
  BillingEntity,
  BillingDetailsData,
  BillingDetailsOutput,
} from "./BillingDetails";
import { InfoIcon } from "@chakra-ui/icons";

// --- Types ---
export interface TypesenseConfig {
  host: string;
  apiKey: string;
}

export interface FieldConfig {
  name: string;
  isVisible?: boolean;
  isDisabled?: boolean;
  isRequired?: boolean;
}

export interface GaraazPlaceEnquiryFormValues {
  selectedCustomer: any;
  customerAddress: any;
  orderDateTime: string;
  partsTableData: PartTableRow[];
  carMaker: string;
  licensePlateNumber: string;
  carModel: string;
  vinNumber: string;
  billingDetails: BillingDetailsOutput | null;
  media: { mediaId: string; path: string; originalName: string }[];
  remarks: string;
  /** Challan-specific */
  workshopCode?: string;
  associatedBranchName?: string;
}

export interface GaraazPlaceEnquiryFormProps {
  authToken: string;
  apiBaseUrl: string;
  typesenseConfig: TypesenseConfig | null;
  fields?: Record<
    string,
    {
      isVisible?: boolean;
      isDisabled?: boolean;
      isRequired?: boolean;
      value?: string;
    }
  >;
  onSubmit: (values: GaraazPlaceEnquiryFormValues) => void | Promise<void>;
  prefilledValues?: Partial<GaraazPlaceEnquiryFormValues>;
  submitLabel?: string;
  onCancel?: () => void;
  isSubmitting?: boolean;
  /** Toast/notification fn: (opts: { description: string; status: 'success' | 'error' }) => void */
  onNotification?: (opts: {
    description: string;
    status: "success" | "error";
  }) => void;
  /** Extra headers (e.g. clientId) for API calls */
  additionalHeaders?: Record<string, string>;
  /** Backend part search when typesenseConfig is null */
  searchPartsFn?: (partNumber: string) => Promise<any[]>;
  /** Format customer display in search dropdown (e.g. "Name-(phone)") */
  searchFormatDisplay?: (data: any) => string;
  /** URL to fetch suppliers for workshop code lookup (e.g. "/v2/suppliers?isPopulated=true") */
  suppliersUrl?: string;
  /** Override customer search URL (e.g. "/v1/marutiworkshop/search?q=" for challan flow). Falls back to default xpress customer search. */
  customerSearchUrl?: string;
  /** Client type (e.g. "placeEnquiry" or "challan") */
  clientType?: "placeEnquiry" | "challan";
  /** Whether to add country code to search (e.g. "91" for mobile numbers) */
  addCountryCodeToSearch?: boolean;
  /** Optional list of allowed batch codes for line items */
  possibleBatchCodes?: string[];
  /** If true, each line item must have a batchCode */
  requireBatchCode?: boolean;
  /** Use shared PartsSearch (HTTP API) in part conversion instead of inline Typesense input. */
  useSharedPartsSearch?: boolean;
  /** Emphasize query matches in part search results. Default true. */
  enablePartSearchHighlighting?: boolean;
}

const LABELS = {
  SEARCH_CUSTOMER: "Search Customer",
  ENTER_NAME_MOBILE: "Enter name or mobile number",
  CUSTOMER_ADDRESS: "Customer Address",
  DATE_TIME: "Date & Time",
  ADD_MEDIA: "+ Add",
  PLACE_ORDER: "Place Enquiry",
  FILE_LIMIT: "Cannot select more than 5 files",
  ORDER_ERROR: "We're unable to process your order. Please try again later.",
  MEDIA_UPLOAD_ERROR: "Unable to upload some or all media.",
  MEDIA_DELETE_ERROR: "Unable to delete media at this time",
};

const defaultDateTime = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

function convertToFlatStructure(
  nested: BillingDetailsOutput | null,
): BillingDetailsData {
  if (!nested) {
    return {
      billingEntity: BillingEntity.CUSTOMER,
      gst: "",
      name: "",
      addressLine1: "",
      addressLine2: "",
      city: "",
      state: "",
      pinCode: "",
      country: "India",
    };
  }
  return {
    billingEntity: nested.billingEntity,
    gst: nested.gst ?? "",
    name: nested.name,
    addressLine1: nested.address.addressLine1,
    addressLine2: nested.address.addressLine2 ?? "",
    city: nested.address.city,
    state: nested.address.state,
    pinCode: nested.address.pinCode,
    country: nested.address.country,
    isSupportingBillRequired: nested.isSupportingBillRequired,
    billingCustomerId: nested.billingCustomerId,
    billingCustomerInsurerFamily: nested.billingCustomerInsurerFamily,
    insuranceClaimId: nested.insuranceClaimId,
    spocDetails: nested.spocDetails,
    surveyorDetails: nested.surveyorDetails,
  };
}

function combineDuplicateParts(
  parts: { partNumber: string; orderedQuantity: number }[],
) {
  return parts.reduce<{ partNumber: string; orderedQuantity: number }[]>(
    (acc, cur) => {
      const idx = acc.findIndex((p) => p.partNumber === cur.partNumber);
      if (idx >= 0) acc[idx].orderedQuantity += cur.orderedQuantity;
      else acc.push({ ...cur });
      return acc;
    },
    [],
  );
}

const partSchema = YupObject().shape({
  partNumber: YupString().trim().required("Part number is missing"),
  orderedQuantity: YupNumber()
    .typeError("Quantity must be a number")
    .positive("Quantity must be greater than zero")
    .required("Quantity is missing"),
});

const GaraazPlaceEnquiryForm: React.FC<GaraazPlaceEnquiryFormProps> = ({
  authToken,
  apiBaseUrl,
  typesenseConfig,
  fields = {},
  onSubmit,
  prefilledValues = {},
  submitLabel = LABELS.PLACE_ORDER,
  isSubmitting = false,
  onNotification,
  additionalHeaders = {},
  searchPartsFn: _searchPartsFn,
  searchFormatDisplay,
  suppliersUrl,
  customerSearchUrl,
  clientType = "placeEnquiry",
  addCountryCodeToSearch = false,
  possibleBatchCodes,
  requireBatchCode = false,
  useSharedPartsSearch = false,
  enablePartSearchHighlighting = true,
}) => {
  const show = (key: string) => fields[key]?.isVisible !== false;

  const [selectedCustomer, setSelectedCustomer] = useState<any>(
    prefilledValues.selectedCustomer ?? null,
  );
  const [locationOptions, setLocationOptions] = useState<any[]>([]);
  const [shipmentLocationId, setShipmentLocationId] = useState<string>("");
  const [selectedDateTime, setSelectedDateTime] = useState<string>(
    prefilledValues.orderDateTime ?? defaultDateTime(),
  );
  const [searchValue, setSearchValue] = useState("");
  const [media, setMedia] = useState<
    { mediaId: string; path: string; originalName: string }[]
  >(prefilledValues.media ?? []);
  const [isMediaUploading, setIsMediaUploading] = useState(false);
  const [isMediaDeleting, setIsMediaDeleting] = useState(false);
  const [remarks, setRemarks] = useState(prefilledValues.remarks ?? "");
  const [partsTableData, setPartsTableData] = useState<PartTableRow[]>(
    prefilledValues.partsTableData ?? [],
  );
  const [ifManuallyAddedParts, setIfManuallyAddedParts] = useState(false);
  const [quantityZeroError, setQuantityZeroError] = useState<any[]>([]);
  const [carMaker, setCarMaker] = useState(
    prefilledValues?.carMaker ?? fields?.carMaker?.value ?? "",
  );
  const [licensePlateNumber, setLicensePlateNumber] = useState(
    prefilledValues.licensePlateNumber ?? "",
  );
  const [carModel, setCarModel] = useState(prefilledValues.carModel ?? "");
  const [vinNumber, setVinNumber] = useState(prefilledValues.vinNumber ?? "");
  const [billingDetails, setBillingDetails] =
    useState<BillingDetailsOutput | null>(
      prefilledValues.billingDetails ?? null,
    );
  const [workshopCode, setWorkshopCode] = useState(
    prefilledValues.workshopCode ?? "",
  );
  const [associatedBranchName, setAssociatedBranchName] = useState(
    prefilledValues.associatedBranchName ?? "",
  );
  const [suppliersCache, setSuppliersCache] = useState<any[]>([]);

  // Excel import state
  const [sheetName, setSheetName] = useState("");
  const [productIdColumnName, setProductIdColumnName] = useState("");
  const [productQuantityColumnName, setProductQuantityColumnName] =
    useState("");
  const [selectedBulkFile, setSelectedBulkFile] = useState<File | null>(null);
  const [selectedBulkFileName, setSelectedBulkFileName] =
    useState("No File Selected");
  const [isFileProcessing, setIsFileProcessing] = useState(false);
  const [isFileProcessed, setIsFileProcessed] = useState(false);
  const [isFileToPartProcessing, setIsFileToPartProcessing] = useState(false);
  const [fileReadingProgress, setFileReadingProgress] = useState(0);
  const [partDocumentConversionProgress, setPartDocumentConversionProgress] =
    useState(0);
  const [processedParts, setProcessedParts] = useState<
    { partNumber: string; orderedQuantity: number }[]
  >([]);
  const [sheetNameError, setSheetNameError] = useState("");
  const [productIdColumnNameError, setProductIdColumnNameError] = useState("");
  const [productQuantityColumnNameError, setProductQuantityColumnNameError] =
    useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();

  const axiosInstance = React.useMemo(
    () =>
      axios.create({
        baseURL: apiBaseUrl,
        headers: { Authorization: authToken, ...additionalHeaders },
      }),
    [apiBaseUrl, authToken, JSON.stringify(additionalHeaders)],
  );

  const {
    carMakersData,
    isLoadingCarMakers,
    stateCityData,
    isLoadingStateCity,
    insurersData,
    isLoadingInsurers,
    brandsData,
    insuranceSpocUsers,
    insuranceSurveyorUsers,
    isLoadingInsuranceSpocUsers,
    isLoadingInsuranceSurveyorUsers,
    services,
  } = usePlaceEnquiryData(apiBaseUrl, authToken);

  const toast = useCallback(
    (opts: { description: string; status: string }) => {
      onNotification?.(
        opts as { description: string; status: "success" | "error" },
      );
    },
    [onNotification],
  );

  // Sync state when prefilledValues changes (e.g. when editing)
  useEffect(() => {
    if (!prefilledValues) return;
    if (prefilledValues.selectedCustomer) {
      const cust = prefilledValues.selectedCustomer;
      setSelectedCustomer(cust);
      setSearchValue(
        cust.name && cust.mobileNumber
          ? `${cust.name}-(${cust.mobileNumber})`
          : cust.name || cust.mobileNumber || "",
      );
      const addrs = cust.addresses ?? [];
      setLocationOptions(addrs);
      if (addrs.length === 1)
        setShipmentLocationId(addrs[0]._id || addrs[0].id || "prefill-0");
      else if (addrs.length > 0)
        setShipmentLocationId(addrs[0]._id || addrs[0].id || "");
    }
  }, [
    prefilledValues?.selectedCustomer?._id,
    prefilledValues?.selectedCustomer?.name,
    prefilledValues?.selectedCustomer?.mobileNumber,
    prefilledValues?.selectedCustomer?.addresses?.length,
  ]);

  // Sync vehicle/other prefilled values when prefilledValues or fields change
  useEffect(() => {
    const maker = prefilledValues?.carMaker ?? fields?.carMaker?.value;
    if (maker != null && maker !== "") setCarMaker(String(maker));
    if (prefilledValues?.licensePlateNumber != null)
      setLicensePlateNumber(String(prefilledValues.licensePlateNumber));
    if (prefilledValues?.carModel != null)
      setCarModel(String(prefilledValues.carModel));
    if (prefilledValues?.vinNumber != null)
      setVinNumber(String(prefilledValues.vinNumber));
    if (prefilledValues?.workshopCode != null)
      setWorkshopCode(String(prefilledValues.workshopCode));
    if (prefilledValues?.associatedBranchName != null)
      setAssociatedBranchName(String(prefilledValues.associatedBranchName));
  }, [
    prefilledValues?.carMaker,
    prefilledValues?.licensePlateNumber,
    prefilledValues?.carModel,
    prefilledValues?.vinNumber,
    prefilledValues?.workshopCode,
    prefilledValues?.associatedBranchName,
    fields?.carMaker?.value,
  ]);

  // Fetch suppliers when Challan fields visible
  useEffect(() => {
    if (!suppliersUrl) return;
    const fetchSuppliers = async () => {
      try {
        const res = await axiosInstance.get(suppliersUrl);
        const data = res?.data;
        setSuppliersCache(Array.isArray(data) ? data : []);
      } catch {
        setSuppliersCache([]);
      }
    };
    fetchSuppliers();
  }, [suppliersUrl, axiosInstance]);

  // Workshop code -> associated branch lookup
  useEffect(() => {
    if (!workshopCode?.trim() || suppliersCache.length === 0) return;
    const code = workshopCode.trim().toUpperCase();
    for (const supplier of suppliersCache) {
      const branches = supplier.branches || [];
      for (const branch of branches) {
        const branchCode = (branch.branchCode || "").toString().toUpperCase();
        if (branchCode && branchCode === code) {
          setAssociatedBranchName(branch.branchName || "");
          return;
        }
      }
    }
  }, [workshopCode, suppliersCache]);

  const handleCustomerChange = (customer: any) => {
    const addrs = customer?.addresses ?? [];
    setLocationOptions(addrs);
    setSelectedCustomer(customer);
    if (addrs.length === 1) setShipmentLocationId(addrs[0]._id);

    if (clientType === "challan") {
      setWorkshopCode(customer.workshopCode);
      setAssociatedBranchName(customer.associateSupplierBranchCode);
      return;
    }

    const supplierAssociations = customer?.supplierAssociations ?? [];
    if (supplierAssociations.length > 0) {
      const supplierAssociation = supplierAssociations[0];
      setWorkshopCode(supplierAssociation.supplierCode);
      const supplier = suppliersCache.find(
        (s) => s._id === supplierAssociation.supplierId,
      );
      if (supplier) {
        setAssociatedBranchName(supplier.branchCode);
      }
    }
  };

  const handleClearCustomer = () => {
    setLocationOptions([]);
    setSelectedCustomer(null);
    setShipmentLocationId("");
    setSearchValue("");
  };

  const handleBulkPlaceOrderSubmit = () => {
    if (!selectedBulkFile) return;
    setIsFileProcessing(true);
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array((e.target?.result as ArrayBuffer) ?? []);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetNameInWorkBook = workbook.SheetNames.find(
          (n: string) => n.toLowerCase() === sheetName.toLowerCase(),
        );
        if (!sheetNameInWorkBook) {
          setSheetNameError("Sheet does not Exist in the Excel Sheet Uploaded");
          setIsFileProcessing(false);
          return;
        }
        setSheetNameError("");
        const worksheet = workbook.Sheets[sheetNameInWorkBook];
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          blankrows: false,
        });
        jsonData[0] = (jsonData[0] as string[]).map((s) => s.toLowerCase());
        const productIdIndex = (jsonData[0] as string[]).indexOf(
          productIdColumnName.toLowerCase(),
        );
        const productQuantityIndex = (jsonData[0] as string[]).indexOf(
          productQuantityColumnName.toLowerCase(),
        );
        if (productIdIndex === -1)
          setProductIdColumnNameError("Product ID Column Name does not Exist");
        else setProductIdColumnNameError("");
        if (productQuantityIndex === -1)
          setProductQuantityColumnNameError(
            "Product Quantity Column Name does not Exist",
          );
        else setProductQuantityColumnNameError("");
        if (productIdIndex === -1 || productQuantityIndex === -1) {
          setIsFileProcessing(false);
          return;
        }
        jsonData.shift();
        let parts: { partNumber: string; orderedQuantity: number }[] = [];
        try {
          parts = (jsonData as (string | number)[][]).map((part, index) => {
            setFileReadingProgress(((index + 1) / jsonData.length) * 100);
            const validated = partSchema.validateSync(
              {
                partNumber: part[productIdIndex],
                orderedQuantity: part[productQuantityIndex],
              },
              { abortEarly: false },
            );
            return {
              partNumber: validated.partNumber,
              orderedQuantity: validated.orderedQuantity,
            };
          });
        } catch (err) {
          if (err instanceof ValidationError) {
            toast({ description: err.errors.join(", "), status: "error" });
          } else if (err instanceof Error) {
            toast({ description: err.message, status: "error" });
          }
          setIsFileProcessing(false);
          setFileReadingProgress(0);
          return;
        }
        setProcessedParts(combineDuplicateParts(parts));
        setIsFileProcessed(true);
      } finally {
        setIsFileProcessing(false);
        setFileReadingProgress(0);
      }
    };
    reader.readAsArrayBuffer(selectedBulkFile);
  };

  const processPartsTableData = (
    fetchedPartsData: any[],
    processed: { partNumber: string; orderedQuantity: number }[],
  ) =>
    fetchedPartsData.map((fp) => {
      const idx = processed.findIndex((p) => p.partNumber === fp.partNumber);
      return {
        _id: fp._id,
        "Part Number": fp.partNumber,
        "Part Name": fp.partName,
        orderedQuantity: processed[idx]?.orderedQuantity ?? 0,
        MRP: fp.mrp,
        actualMrp: fp.mrp,
        brandId: fp.brandId,
        discount: clientType === "challan" ? fp.discount ?? 0 : undefined,
        Brand: fp.brand,
        "HSN code": fp.hsnCode,
        "Root Part Number": fp.rootPartNumber,
      };
    });

  const handlePerfectClick = async () => {
    setPartDocumentConversionProgress(0);
    setIsFileToPartProcessing(true);
    const data = await services.getSearchedPartListBulk(processedParts, (p) => {
      setPartDocumentConversionProgress(p);
      if (p === 100) {
        setTimeout(() => {
          setIsFileToPartProcessing(false);
          setPartDocumentConversionProgress(0);
        }, 400);
      }
    });
    const newParts = processPartsTableData(data, processedParts);
    setPartsTableData((prev) => {
      const existingPartNumbers = new Set(
        prev.map((p) =>
          (p["Part Number"] ?? "").toString().trim().toLowerCase(),
        ),
      );
      const uniqueNewParts = newParts.filter(
        (np) =>
          !existingPartNumbers.has(
            (np["Part Number"] ?? "").toString().trim().toLowerCase(),
          ),
      );
      return [...uniqueNewParts, ...prev];
    });
    setIsFileProcessed(false);
    setIsFileProcessing(false);
    setSheetName("");
    setProductIdColumnName("");
    setProductQuantityColumnName("");
    setSelectedBulkFile(null);
    setSelectedBulkFileName("No File Selected");
    setProcessedParts([]);
    onClose();
  };

  const handleReDoClick = () => {
    setIsFileProcessed(false);
    setIsFileProcessing(false);
    setProcessedParts([]);
    setSheetNameError("");
    setProductIdColumnNameError("");
    setProductQuantityColumnNameError("");
    setSelectedBulkFile(null);
    setSelectedBulkFileName("No File Selected");
    setSheetName("");
    setProductIdColumnName("");
    setProductQuantityColumnName("");
  };

  const handleAddMediaClick = async () => {
    const configRes = await axiosInstance.get("/mobile/config");
    const mimeTypes: string[] = JSON.parse(
      configRes.data?.order_media_type ?? "[]",
    );
    if (media.length >= 5) {
      toast({ description: LABELS.FILE_LIMIT, status: "error" });
      return;
    }
    const input = document.createElement("input");
    input.type = "file";
    input.accept = mimeTypes.join(",");
    input.multiple = true;
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (!files || media.length + files.length > 5) {
        toast({ description: LABELS.FILE_LIMIT, status: "error" });
        return;
      }
      setIsMediaUploading(true);
      try {
        for (let i = 0; i < files.length; i++) {
          const fd = new FormData();
          fd.append("media", files[i]);
          const res = await services.addMedia(fd);
          setMedia((prev) => [
            ...prev,
            {
              mediaId: res[0]._id,
              path: res[0].path,
              originalName: files[i].name,
            },
          ]);
        }
      } catch {
        toast({ description: LABELS.MEDIA_UPLOAD_ERROR, status: "error" });
      } finally {
        setIsMediaUploading(false);
      }
    };
    input.click();
  };

  const totalAmount = partsTableData.reduce(
    (acc, part) =>
      acc + (Number(part.MRP) || 0) * (Number(part.orderedQuantity) || 1),
    0,
  );

  const handleDeleteMediaClick = async (index: number) => {
    setIsMediaDeleting(true);
    try {
      await services.deleteMedia(media[index].mediaId);
      setMedia((prev) => prev.filter((_, i) => i !== index));
    } catch {
      toast({ description: LABELS.MEDIA_DELETE_ERROR, status: "error" });
    } finally {
      setIsMediaDeleting(false);
    }
  };

  const handleSubmit = async () => {
    const customerAddress = locationOptions.find(
      (a: any) => a._id === shipmentLocationId,
    );
    const values: GaraazPlaceEnquiryFormValues = {
      selectedCustomer,
      customerAddress,
      orderDateTime: selectedDateTime,
      partsTableData,
      carMaker,
      licensePlateNumber,
      carModel,
      vinNumber,
      billingDetails,
      media,
      remarks,
      workshopCode,
      associatedBranchName,
    };
    await onSubmit(values);
  };

  const typesenseForSearch = typesenseConfig
    ? { host: typesenseConfig.host, apiKey: typesenseConfig.apiKey }
    : null;

  const sectionCardStyle = {
    bg: "white",
    borderRadius: "lg",
    boxShadow: "md",
    p: 6,
    width: "100%",
  };

  return (
    <Flex direction="column" bgColor="gray.100" p={6} gap={6}>
      {/* Customer Details */}
      {show("customerDetails") && (
        <Box {...sectionCardStyle}>
          <HStack w="100%" flexDirection="column" alignItems="stretch">
            <Box width="100%" mb={5} borderBottom="1px solid lightgrey">
              <Text fontWeight="bold">Customer Details</Text>
            </Box>
            <Box
              display="flex"
              alignItems="flex-end"
              width="100%"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={4}
            >
              {show("customerSearch") && (
                <FormControl width={"100%"}>
                  <FormLabel fontSize="smaller" fontWeight="medium">
                    {LABELS.SEARCH_CUSTOMER} *
                  </FormLabel>
                  {fields?.customerSearch?.isDisabled ? (
                    <Input
                      value={searchValue}
                      readOnly
                      bgColor="white"
                      size="md"
                    />
                  ) : (
                    <>
                      <GaraazSearch
                        placeholder={
                          LABELS.ENTER_NAME_MOBILE +
                          (clientType === "challan" ? " or workshop code" : "")                        }
                        baseSearchUrl={
                          customerSearchUrl ??
                          placeEnquiryEndpoints.searchXpressCustomer
                        }
                        addCountryCodeToSearch={addCountryCodeToSearch}
                        axios={axiosInstance}
                        onSelect={handleCustomerChange}
                        onClear={handleClearCustomer}
                        onChange={setSearchValue}
                        acceptMobileNumber
                        hideSearchIcon
                        idField="_id"
                        valueField="name"
                        value={searchValue}
                        formatDisplay={searchFormatDisplay}
                      />
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Icon as={InfoIcon} color="gray.500" fontSize={"sm"} />
                        <Text fontSize="smaller" fontWeight="medium">
                          You can search by the last digits of mobile number,
                          last letters of customer code, or any part of customer
                          name (e.g. 1234, ABC, or John).
                        </Text>
                      </Box>
                    </>
                  )}
                </FormControl>
              )}
              {show("deliveryAddress") && (
                <FormControl width={{ base: "100%", md: "32%" }}>
                  <FormLabel fontSize="smaller" fontWeight="medium">
                    {LABELS.CUSTOMER_ADDRESS}
                  </FormLabel>
                  {fields?.deliveryAddress?.isDisabled || !selectedCustomer ? (
                    <Input
                      value={
                        !selectedCustomer
                          ? ""
                          : (() => {
                              const addr = locationOptions.find(
                                (a: any) =>
                                  (a._id || a.id) === shipmentLocationId,
                              );
                              return addr
                                ? [
                                    addr.addressLine1,
                                    addr.addressLine2,
                                    addr.city,
                                    addr.state,
                                  ]
                                    .filter(Boolean)
                                    .join(", ")
                                : "";
                            })()
                      }
                      isDisabled={true}
                      bgColor="white"
                      size="md"
                      placeholder={
                        !selectedCustomer
                          ? "Please select a customer first"
                          : undefined
                      }
                    />
                  ) : (
                    <select
                      style={{
                        width: "100%",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid",
                      }}
                      value={shipmentLocationId}
                      onChange={(e) => setShipmentLocationId(e.target.value)}
                    >
                      <option value="">Select...</option>
                      {locationOptions.map((addr: any) => (
                        <option key={addr._id} value={addr._id}>
                          {`${addr.addressLine1}, ${addr?.addressLine2 ?? ""} ${addr.city}, ${addr.state}`}
                        </option>
                      ))}
                    </select>
                  )}
                </FormControl>
              )}
              {show("orderDateTime") && (
                <FormControl width={{ base: "100%", md: "32%" }}>
                  <FormLabel fontSize="smaller" fontWeight="medium">
                    {LABELS.DATE_TIME}
                  </FormLabel>
                  <Input
                    type="datetime-local"
                    value={selectedDateTime}
                    onChange={(e) => setSelectedDateTime(e.target.value)}
                    max={new Date().toISOString().slice(0, 16)}
                  />
                </FormControl>
              )}
              {show("workshopCode") && (
                <FormControl width={{ base: "100%", md: "32%" }}>
                  <FormLabel fontSize="smaller" fontWeight="medium">
                    Customer Code *
                  </FormLabel>
                  <Input
                    placeholder={
                      !selectedCustomer
                        ? "Please select a customer first"
                        : "Enter customer code *"
                    }
                    value={workshopCode}
                    onChange={(e) => setWorkshopCode(e.target.value)}
                    isDisabled={!selectedCustomer}
                  />
                </FormControl>
              )}
              {show("associatedBranchName") && (
                <FormControl width={{ base: "100%", md: "32%" }}>
                  <FormLabel fontSize="smaller" fontWeight="medium">
                    Associated Branch Code
                  </FormLabel>
                  <Input
                    placeholder={
                      !selectedCustomer
                        ? "Please select a customer first"
                        : "Enter branch code"
                    }
                    value={associatedBranchName}
                    onChange={(e) => setAssociatedBranchName(e.target.value)}
                    isDisabled={!selectedCustomer}
                  />
                </FormControl>
              )}
            </Box>
          </HStack>
        </Box>
      )}

      {/* Part Numbers + Excel Import */}
      {show("partNumbers") && (
        <Box {...sectionCardStyle}>
          <HStack flexDirection="column" alignItems="stretch" w="100%">
            <Box
              mb={5}
              borderBottom="1px solid lightgrey"
              display="flex"
              justifyContent="space-between"
              padding="5px"
            >
              <Text fontWeight="bold">Part Numbers</Text>
              {show("importParts") && (
                <Button
                  onClick={onOpen}
                  size="inherit"
                  padding="4px 10px"
                  fontSize="smaller"
                  isDisabled={ifManuallyAddedParts}
                  leftIcon={<Icon as={GrDownload} color="white" />}
                  backgroundColor="#1e283b"
                  textColor="white"
                  _hover={{ backgroundColor: "#1e283b", textColor: "white" }}
                >
                  Import
                </Button>
              )}
            </Box>

            <Modal isOpen={isOpen} onClose={onClose} size="6xl">
              <ModalOverlay />
              <ModalContent width="100%">
                <ModalHeader>Import Parts</ModalHeader>
                <ModalCloseButton />
                <ModalBody width="100%">
                  {isFileProcessing && (
                    <Box>
                      <Progress value={fileReadingProgress} />
                    </Box>
                  )}
                  {!isFileProcessed && (
                    <>
                      <Box borderBottom="1px solid lightgrey" mb={5}>
                        <Text fontSize="large" fontWeight="semibold">
                          File Upload
                        </Text>
                      </Box>
                      <FormControl
                        display="flex"
                        alignItems="center"
                        height="40px"
                        mb={5}
                      >
                        <FormLabel
                          margin={0}
                          width="25%"
                          fontWeight="normal"
                          fontSize="medium"
                        >
                          Order File
                        </FormLabel>
                        <Input
                          disabled={isFileProcessing}
                          type="file"
                          opacity={0}
                          position="absolute"
                          width="0.1px"
                          height="0.1px"
                          zIndex={-100}
                          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) {
                              setSelectedBulkFile(f);
                              setSelectedBulkFileName(f.name);
                            }
                          }}
                        />
                        <FormLabel
                          cursor="pointer"
                          border="1px solid lightgrey"
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          margin={0}
                          borderRadius="5px"
                          height="inherit"
                          paddingLeft="0.7em"
                          width="20%"
                        >
                          <Text fontSize="small">{selectedBulkFileName}</Text>
                          <Icon
                            as={GrDownload}
                            padding="5px"
                            borderLeft="1px solid lightgrey"
                            height="80%"
                          />
                        </FormLabel>
                      </FormControl>
                      <Box mt={10}>
                        <Box borderBottom="1px solid lightgrey" mb={5}>
                          <Text fontSize="large" fontWeight="semibold">
                            Define Column Mappings (Case-Insensitive)
                          </Text>
                        </Box>
                        <FormControl mb={5}>
                          <FormLabel margin={0} width="25%" fontSize="medium">
                            Sheet Name
                          </FormLabel>
                          <Tooltip
                            label="Sheet name in Excel file which has order details."
                            placement="right"
                          >
                            <Box>
                              <Input
                                disabled={isFileProcessing}
                                type="text"
                                width="fit-content"
                                value={sheetName}
                                onChange={(e) => setSheetName(e.target.value)}
                              />
                              {sheetNameError && (
                                <Text color="red" fontSize="x-small">
                                  {sheetNameError}
                                </Text>
                              )}
                            </Box>
                          </Tooltip>
                        </FormControl>
                        <FormControl mb={5}>
                          <FormLabel margin={0} width="25%" fontSize="medium">
                            Product Id Column Name
                          </FormLabel>
                          <Tooltip
                            label="Product ID Column name in Excel file."
                            placement="right"
                          >
                            <Box>
                              <Input
                                disabled={isFileProcessing}
                                type="text"
                                width="fit-content"
                                value={productIdColumnName}
                                onChange={(e) =>
                                  setProductIdColumnName(e.target.value)
                                }
                              />
                              {productIdColumnNameError && (
                                <Text color="red" fontSize="x-small">
                                  {productIdColumnNameError}
                                </Text>
                              )}
                            </Box>
                          </Tooltip>
                        </FormControl>
                        <FormControl mb={5}>
                          <FormLabel margin={0} width="25%" fontSize="medium">
                            Product Quantity Column Name
                          </FormLabel>
                          <Tooltip
                            label="Product Quantity Column name in Excel file."
                            placement="right"
                          >
                            <Box>
                              <Input
                                disabled={isFileProcessing}
                                type="text"
                                width="fit-content"
                                value={productQuantityColumnName}
                                onChange={(e) =>
                                  setProductQuantityColumnName(e.target.value)
                                }
                              />
                              {productQuantityColumnNameError && (
                                <Text color="red" fontSize="x-small">
                                  {productQuantityColumnNameError}
                                </Text>
                              )}
                            </Box>
                          </Tooltip>
                        </FormControl>
                        <Button
                          onClick={handleBulkPlaceOrderSubmit}
                          isDisabled={
                            !selectedBulkFile ||
                            !sheetName ||
                            !productIdColumnName ||
                            !productQuantityColumnName
                          }
                        >
                          Upload
                        </Button>
                      </Box>
                    </>
                  )}
                  {isFileProcessed && (
                    <>
                      <Box mt={10}>
                        <Box borderBottom="1px solid lightgrey" mb={5}>
                          <Text fontSize="large" fontWeight="semibold">
                            Review
                          </Text>
                        </Box>
                        {isFileToPartProcessing && (
                          <Box mb={5}>
                            <Progress value={partDocumentConversionProgress} />
                          </Box>
                        )}
                        <Text color="#0096E0" mb={5} fontSize="medium">
                          Total Parts Added : {processedParts.length}
                        </Text>
                        <TableContainer>
                          <Table variant="simple">
                            <Thead>
                              <Tr>
                                <Th>Part Number</Th>
                                <Th isNumeric>Quantity</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {processedParts.map((part, idx) => (
                                <Tr key={idx}>
                                  <Td>{part.partNumber}</Td>
                                  <Td isNumeric>{part.orderedQuantity}</Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </TableContainer>
                        <Box mt={10} display="flex" justifyContent="flex-end">
                          <Button
                            onClick={handleReDoClick}
                            backgroundColor="#ef233c"
                            _hover={{
                              backgroundColor: "rgba(239, 35, 60,0.8)",
                            }}
                          >
                            Re-Do
                          </Button>
                          <Button
                            ml={3}
                            onClick={handlePerfectClick}
                            backgroundColor="#2a9134"
                            _hover={{ backgroundColor: "rgba(41,145,52,0.8)" }}
                          >
                            Perfect
                          </Button>
                        </Box>
                      </Box>
                    </>
                  )}
                </ModalBody>
              </ModalContent>
            </Modal>

            <Box width="100%">
              <GaraazPartConversion
                typesenseConfig={typesenseForSearch}
                partsTableData={partsTableData}
                setPartsTableData={setPartsTableData}
                quantityZeroError={quantityZeroError}
                setQuantityZeroError={setQuantityZeroError}
                setIfManuallyAddedParts={setIfManuallyAddedParts}
                brandsData={brandsData}
                showDateOfPurchase={show("dateOfPurchase")}
                showMrp={show("mrp")}
                showDiscount={clientType === "challan"}
                todayStr={new Date().toISOString().split("T")[0]}
                enableMrpValidation={clientType === "challan"}
                possibleBatchCodes={possibleBatchCodes ?? []}
                requireBatchCode={requireBatchCode}
                useSharedPartsSearch={useSharedPartsSearch}
                partSearchAxios={useSharedPartsSearch ? axiosInstance : undefined}
                partSearchBrand={carMaker}
                enableSearchHighlighting={enablePartSearchHighlighting}
              />
              {show("totalAmount") && totalAmount > 0 && (
                <Text fontWeight="bold" marginLeft={4}>
                  Total Amount: {String(totalAmount)}
                </Text>
              )}
            </Box>
          </HStack>
        </Box>
      )}

      {/* Media */}
      {show("media") && (
        <Box {...sectionCardStyle}>
          <VStack width="100%" alignItems="stretch">
            <Box
              width="100%"
              mb={5}
              borderBottom="1px solid lightgrey"
              display="flex"
              justifyContent="space-between"
              alignItems="flex-end"
              paddingBottom="5px"
            >
              <Text fontWeight="bold">Media</Text>
              <Button
                onClick={handleAddMediaClick}
                isLoading={isMediaUploading}
                isDisabled={
                  isSubmitting || isMediaDeleting || media.length >= 5
                }
                size="inherit"
                padding="4px 10px"
                fontSize="smaller"
                backgroundColor="#1e283b"
                textColor="white"
                _hover={{ backgroundColor: "#1e283b", textColor: "white" }}
              >
                {LABELS.ADD_MEDIA}
              </Button>
            </Box>
            <Flex width="100%" flexWrap="wrap" gap={2}>
              {media.map((data, idx) => {
                const name = getFileName(data.originalName);
                const ext = getFileExt(data.originalName);
                const shortName = ellipsizeText(name, 20);
                return (
                  <Box
                    key={idx}
                    position="relative"
                    maxWidth="20%"
                    height="200px"
                    minW="120px"
                  >
                    {["jpg", "jpeg", "png"].some((e) =>
                      ext.toLowerCase().endsWith(e),
                    ) ? (
                      <Image
                        src={data.path}
                        alt="Uploaded"
                        height="100%"
                        objectFit="cover"
                      />
                    ) : (
                      <VStack height="100%" alignItems="center">
                        <AiOutlineFile size={80} />
                        <Text fontSize="xs">{`${shortName}.${ext}`}</Text>
                      </VStack>
                    )}
                    <Button
                      position="absolute"
                      top="-5"
                      right="0"
                      padding={0}
                      aria-label="delete media"
                      isDisabled={isSubmitting || isMediaUploading}
                      onClick={() => handleDeleteMediaClick(idx)}
                      height="fit-content"
                      width="fit-content"
                      backgroundColor="transparent"
                      color="red"
                      minWidth="none"
                      borderRadius="50%"
                    >
                      <Cancel fontSize="small" />
                    </Button>
                  </Box>
                );
              })}
            </Flex>
          </VStack>
        </Box>
      )}

      {/* Vehicle Info */}
      {show("vehicleInfo") && (
        <Box {...sectionCardStyle}>
          <VehicleInfoCard
            licensePlateNumber={licensePlateNumber}
            carModel={carModel}
            vinNumber={vinNumber}
            carMaker={carMaker}
            isCarMakerDisabled={fields?.carMaker?.isDisabled ?? false}
            carMakersData={carMakersData}
            isLoadingMakers={isLoadingCarMakers}
            onLicensePlateChange={setLicensePlateNumber}
            onCarModelChange={setCarModel}
            onVinNumberChange={setVinNumber}
            onCarMakerChange={setCarMaker}
            isLicensePlateNumberRequired={
              fields?.licensePlateNumber?.isRequired ?? false
            }
          />
        </Box>
      )}

      {/* Billing Details */}
      {show("billingDetails") && (
        <Box {...sectionCardStyle}>
          <BillingDetails
            billingDetails={convertToFlatStructure(billingDetails)}
            onBillingDetailsChange={setBillingDetails}
            selectedCustomer={selectedCustomer}
            stateCityData={stateCityData}
            insurersData={insurersData}
            isLoadingStateCity={isLoadingStateCity}
            isLoadingInsurers={isLoadingInsurers}
            isSupportingBillVisible={show("supportingBillRequired")}
            isClaimIdVisible={show("insuranceClaimId")}
            insuranceSpocUsers={insuranceSpocUsers}
            insuranceSurveyorUsers={insuranceSurveyorUsers}
            isLoadingInsuranceSpocUsers={isLoadingInsuranceSpocUsers}
            isLoadingInsuranceSurveyorUsers={isLoadingInsuranceSurveyorUsers}
          />
        </Box>
      )}

      {/* Remarks */}
      {show("remarks") && (
        <Box {...sectionCardStyle}>
          <Box mb={5} borderBottom="1px solid lightgrey">
            <Text fontWeight="bold">Remarks</Text>
          </Box>
          <FormControl width="100%">
            <Textarea
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              bgColor="white"
            />
          </FormControl>
        </Box>
      )}

      <Box width="100%" pt={2} textAlign="right">
        <Button
          width="fit-content"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={isMediaUploading || isMediaDeleting}
          ml="auto"
          mt={0}
          backgroundColor="#1e283b"
          textColor="white"
          _hover={{ backgroundColor: "#1e283b", textColor: "white" }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Flex>
  );
};

export const FormSection: React.FC<{
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}> = ({ title, children, action }) => (
  <Box width="100%">
    <Box
      mb={5}
      borderBottom="1px solid lightgrey"
      pb={2}
      width="100%"
      display="flex"
      justifyContent={action ? "space-between" : "flex-start"}
      alignItems="flex-end"
      paddingBottom={action ? "5px" : undefined}
    >
      <Text fontWeight="bold" lineHeight="normal" height="fit-content">
        {title}
      </Text>
      {action}
    </Box>
    <Box mt={4}>{children}</Box>
  </Box>
);

export const FormFieldGrid: React.FC<{
  columns?: number;
  children: React.ReactNode;
}> = ({ columns = 2, children }) => (
  <Box
    display="grid"
    gridTemplateColumns={{ base: "1fr", md: `repeat(${columns}, 1fr)` }}
    gap={{ base: 3, md: 4 }}
  >
    {children}
  </Box>
);

export default GaraazPlaceEnquiryForm;
